import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchLsResult } from "../../functions/fetchLsResult";
import { lsLastResultIdKey } from "../../functions/lsPlayedFlag";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

const s3Url = (key) => (key ? `${process.env.REACT_APP_S3_PATH || ""}${key}` : "");

function findLabelIndex(settings, skillLabel) {
  if (!settings || !skillLabel) return null;
  for (let n = 1; n <= 6; n++) {
    if (settings[`option${n}`] === skillLabel) return n;
  }
  return null;
}

function findCategoryIcon(settings, skillLabel) {
  const n = findLabelIndex(settings, skillLabel);
  return n ? settings[`cat_icon${n}`] || "" : "";
}

/**
 * Admin-configured heading + message for a label as primary/secondary. The
 * resolved skill name (the actual DB-computed answer) always renders bolded
 * at the end of the heading line, regardless of what static text the admin
 * put in the Heading field — matching the old app's "Your primary learning
 * style is <b>Kinesthetic</b>" pattern instead of requiring admins to retype
 * the name into every heading themselves.
 */
function getSkillMessage(settings, skillLabel, kind) {
  const n = findLabelIndex(settings, skillLabel);
  const text = n ? settings?.[`${kind}_text_${n}`]?.trim() : "";
  const heading = (n && settings[`${kind}_heading_${n}`]) || (kind === "primary" ? "As Primary:" : "As Secondary:");
  return { heading, text };
}

function ThankYou() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentTitle(settings?.tab_name);

  useEffect(() => {
    if (!adminToken) {
      navigate("/login");
      return;
    }
    const id = sessionStorage.getItem(lsLastResultIdKey(adminToken));
    if (!id) {
      navigate("/rules");
      return;
    }
    (async () => {
      try {
        const res = await fetchLsResult({ id, token: adminToken });
        setResult(res.result || null);
        setSettings(res.settings || null);
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to load result", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [adminToken, navigate]);

  if (loading) {
    return (
      <div className="ls-page">
        <div className="ls-loading">Loading...</div>
      </div>
    );
  }

  if (!result) return null;

  const badgeEnabled = settings?.badge_enabled == null ? true : !!settings.badge_enabled;
  const icon = badgeEnabled ? findCategoryIcon(settings, result.primary_skill) : "";
  const customText = settings?.custom_text_thank_you_page || "";
  const headingText = settings?.thankyou_heading || "Your Learning Style";

  return (
    <div
      className={`ls-page ls-thankyou-page${badgeEnabled ? "" : " ls-thankyou-badge-off"}`}
      style={{ "--ls-text-color": settings?.text_color || "#1f2937" }}
    >
      {badgeEnabled && (
        <>
          <h2 className="ls-thankyou-title">{headingText}</h2>

          {icon && <img src={s3Url(icon)} alt={result.primary_skill} className="ls-thankyou-icon" />}

          {(() => {
            const primaryMsg = getSkillMessage(settings, result.primary_skill, "primary");
            return (
              <div className="ls-thankyou-message-block">
                <p className="ls-thankyou-message-heading">
                  {primaryMsg.heading} <strong>{result.primary_skill}</strong>
                </p>
                {primaryMsg.text && <p className="ls-thankyou-message-text">{primaryMsg.text}</p>}
              </div>
            );
          })()}
          {result.secondary_skill &&
            (() => {
              const secondaryMsg = getSkillMessage(settings, result.secondary_skill, "secondary");
              return (
                <div className="ls-thankyou-message-block">
                  <p className="ls-thankyou-message-heading">
                    {secondaryMsg.heading} <strong>{result.secondary_skill}</strong>
                  </p>
                  {secondaryMsg.text && <p className="ls-thankyou-message-text">{secondaryMsg.text}</p>}
                </div>
              );
            })()}
        </>
      )}

      {customText && <p className="ls-thankyou-custom-text">{customText}</p>}
    </div>
  );
}

export default ThankYou;
